








































import OtStandardHeaderArchetype from '@/components/global/archetypes/ot-standard-header-archetype.vue';
import OtButton from '@/components/global/ot-button.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtTabs from '@/components/global/ot-tabs.vue';
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';

import { IInputAction, IInputTab } from '@/components/global/common-models';
import OtApi, { executeApi } from '@/services/api.service';
import { vxm } from '@/store';
import { Calendar } from './models/calendar-models';
import { Organisation } from './models/organisation-models';

import { AconexImportDialogParams } from '@/components/documents/aconex-import-dialog/aconex-import-dialog-models';
import {
  AddEditDialogMode,
  AddEditDocumentDialogParams,
} from '@/components/documents/add-edit-document-dialog/add-edit-document-dialog-models';
import { SelectDocumentDialogParams } from '@/components/documents/select-document-dialog/select-document-dialog-models';
import { doAconexImport } from '@/services/aconex.service';
import { PostUpdateDocumentAssociationsRequestModel } from '@/services/generated/api';
import { areSame } from '@/utils/string-utils';
import OtInviteUserDialog, { InviteUserDialogParams } from '../invitations/ot-invite-user-dialog.vue';
import { OrganisationUser } from './models/organisation-user-models';
import OtOrganisationDropdownSelector from './organisations/ot-organisation-dropdown-selector.vue';
import {
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_CALENDARS,
  ROUTE_SETTINGS_DOCUMENTS,
  ROUTE_SETTINGS_USERS,
} from './settings-routes';

@Component({
  components: {
    OtStandardHeaderArchetype,
    OtTabs,
    OtLoadingSpinner,
    OtOrganisationDropdownSelector,
    OtButton,
    OtInviteUserDialog,
  },
})
export default class OtSettingsLayout extends Vue {
  // * PROPS

  // * REFS

  // * DATA

  private newCalendarButton: IInputAction = {
    actionName: 'New Calendar',
    action: { name: 'AddCalendar' },
  };

  private newOrgButton: IInputAction = {
    actionName: 'New Organisation',
    // this is not actually used, we disable when this is displayed
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    action: () => {},
  };
  private newUserButton: IInputAction = {
    actionName: 'New User',
    action: () => this.createNewUser(),
  };

  private newDocumentButton: IInputAction = {
    actionName: 'New Document',
    action: () => this.createNewDocument(),
  };
  private importDocumentButton: IInputAction = {
    actionName: 'Add Existing Document',
    action: () => this.importDocument(),
  };
  private importFromAconexButton: IInputAction = {
    actionName: 'Import From Aconex',
    action: () => this.importFromAconex(),
  };

  private get settingsTabs(): IInputTab[] {
    return [
      {
        tabId: 0,
        tabText: 'Organisation Details',
        tabRoute: {
          params: { orgId: this.orgId },
          name: ROUTE_SETTINGS,
        },
      },
      {
        tabId: 1,
        tabText: 'Calendars',
        tabRoute: {
          params: { orgId: this.orgId },
          name: ROUTE_SETTINGS_CALENDARS,
        },
      },
      {
        tabId: 2,
        tabText: 'Documents',
        tabRoute: {
          params: { orgId: this.orgId },
          name: ROUTE_SETTINGS_DOCUMENTS,
        },
      },
      {
        tabId: 3,
        tabText: 'Users',
        tabRoute: {
          params: { orgId: this.orgId },
          name: ROUTE_SETTINGS_USERS,
        },
      },
    ];
  }

  private api = new OtApi();
  private loading = false;
  private allCalendars: Calendar[] = [];
  private organisation: Organisation | null = null;
  private organisationUsers: OrganisationUser[] = [];
  private lastLoadedCalendarsForOrg = '';

  // * COMPUTED
  private get orgId() {
    return this.$route.params.orgId || vxm.userProfile.defaultOrganisationId || '';
  }

  private get orgList() {
    return vxm.userProfile.userProfile?.organisations || [];
  }

  private get orgName() {
    return this.orgList.find(org => org.gid === this.orgId)?.name || '';
  }

  private get routePath(): string | null {
    return this.$route.path || null;
  }
  private get currentRouteName() {
    return this.$route.name;
  }

  private get primaryContextBtn() {
    switch (this.currentRouteName) {
      case ROUTE_SETTINGS:
        return this.newOrgButton;
      case ROUTE_SETTINGS_USERS:
        return this.newUserButton;
      case ROUTE_SETTINGS_CALENDARS:
        return this.newCalendarButton;
      case ROUTE_SETTINGS_DOCUMENTS:
        return this.newDocumentButton;
      default:
        console.warn(
          `OtSettingsLayout.primaryContextBtn - no primary button defined for '${this.currentRouteName}' - defaulting to newCalendarButton`,
        );
        return this.newCalendarButton;
    }
  }

  private get secondaryContextBtns() {
    switch (this.currentRouteName) {
      case ROUTE_SETTINGS:
        return [];
      case ROUTE_SETTINGS_USERS:
        return [];
      case ROUTE_SETTINGS_CALENDARS:
        return [];
      case ROUTE_SETTINGS_DOCUMENTS:
        return [this.importDocumentButton, this.importFromAconexButton];
      default:
        console.warn(
          `OtSettingsLayout.secondaryContextBtns - no secondary buttons defined for '${this.currentRouteName}' - defaulting to []`,
        );
        return [];
    }
  }

  private get disableContextButton() {
    // this feels a bit light on, but if you can see this page, you're an org user, you can make a new calendar/user/document
    return (
      this.currentRouteName !== ROUTE_SETTINGS_CALENDARS &&
      this.currentRouteName !== ROUTE_SETTINGS_USERS &&
      this.currentRouteName !== ROUTE_SETTINGS_DOCUMENTS
    );
  }

  // * WATCHERS
  @Watch('routePath')
  private async checkRoutePath() {
    this.setOrgNameToBreadcrumb();
    if (this.$route.name === ROUTE_SETTINGS_CALENDARS && !this.allCalendars.length) {
      this.lastLoadedCalendarsForOrg = this.orgId;
      this.loading = true;
      await this.handleReloadCalendars();
      this.loading = false;
    }
    if (
      this.$route.name === ROUTE_SETTINGS_CALENDARS &&
      this.allCalendars.length &&
      !areSame(this.orgId, this.lastLoadedCalendarsForOrg)
    ) {
      this.lastLoadedCalendarsForOrg = this.orgId;

      this.loading = true;
      await this.handleReloadCalendars();
      this.loading = false;
    }

    if (this.$route.name === ROUTE_SETTINGS && (!this.organisation || !areSame(this.organisation.gid, this.orgId))) {
      this.loading = true;
      await this.handleReloadOrganisationDetails();
      this.loading = false;
    }

    if (this.$route.name === ROUTE_SETTINGS_USERS && !this.organisationUsers.length) {
      this.loading = true;
      await this.handleReloadOrganisationUsers();
      this.loading = false;
    }
  }

  // * METHODS
  setOrgNameToBreadcrumb() {
    vxm.breadcrumbs.setOrganisationName({ organisation: this.orgName });
  }

  updateOrg(orgId: string) {
    this.$router.push({ name: this.currentRouteName || ROUTE_SETTINGS, params: { orgId } });
  }

  private async handleReloadCalendars(callback?: () => void) {
    const result = await executeApi(
      () => this.api.organisation().getOrganisationCalendars(this.orgId),
      'Load Calendars',
    );
    if (result.success && result.data && result.data.calendars) {
      this.allCalendars = result.data.calendars.map(c => Calendar.createFromApiResponse(c));
    }

    if (callback) {
      callback();
    }
  }

  private async handleReloadOrganisationDetails() {
    const result = await executeApi(
      () => this.api.organisation().getOrganisationDetails(this.orgId),
      'Load Organisation Details',
    );
    if (result.success && result.data) {
      this.organisation = Organisation.createFromApiResponse(result.data);
    }
  }

  private async handleReloadOrganisationUsers(callback?: () => void) {
    const result = await executeApi(
      () => this.api.organisation().getOrganisationUsers(this.orgId),
      'Load Organisation Users',
    );
    if (result.success && result.data && result.data?.users) {
      this.organisationUsers = result.data.users.map(u => OrganisationUser.createFromApiResponse(u));
    }

    if (callback) {
      callback();
    }
  }

  @Ref('inviteUserDialogRef')
  private readonly inviteUserDialogRef!: OtInviteUserDialog;

  private async createNewUser() {
    const params = new InviteUserDialogParams({
      isReinvite: false,
      organisationGid: this.orgId,
      inviteToOrganisation: true,
    });

    const dialogResult = await this.inviteUserDialogRef.open(params);
    if (dialogResult) {
      this.handleReloadOrganisationUsers();
    }
  }

  private dodgyHackToReloadCurrentView = 1;

  private async createNewDocument() {
    const params = new AddEditDocumentDialogParams({
      addToOrganisation: true,
      targetOrganisationGid: this.orgId,
      context: 'Organisation',
      mode: AddEditDialogMode.Create,
    });
    const result = await vxm.modal.openAddEditDocumentDialog(params);
    if (result.ok) {
      // the documents tab does its own loading. We can't just call handleReloadDocuments here, because it doesn't exist
      // we could somehow pulse a signal down (update a Symbol) that could be watched, but meh
      // we could allow the child to register an onReloadDocuments handler by emitting a setReloadDocuments event, hold it, call it here, but meh
      // incrementing a variable to reload the whole shooting match works well enough.
      // Unfortunately it blows away the search criteria, that might be annoying.
      // If the user is sitting there with a list of 5 documents to create, all with a reference ABC123-something, and they search
      // for ABC-123 to see what needs creating
      // it might be beneficial to retain the search results. If that turns out to be a wanted feature, consider the setReloadDocuments event
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }

  private async importDocument() {
    if (!this.orgId) return;

    const params = new SelectDocumentDialogParams({
      targetOrganisationGid: this.orgId,
      context: 'Organisation',
    });
    const dialogResult = await vxm.modal.openSelectDocumentDialog(params);
    if (dialogResult.ok) {
      for (const selectedDocument of dialogResult.selectedDocuments) {
        const request = new PostUpdateDocumentAssociationsRequestModel({
          addProjectAssociations: [],
          addContractAssociations: [],
          addOrganisationAssociations: [this.orgId],
          removeProjectAssociations: [],
          removeContractAssociations: [],
          removeOrganisationAssociations: [],
        });
        await executeApi(
          () => this.api.documents().postUpdateDocumentAssociations(selectedDocument.gid, request, undefined),
          'Associate Document with Organisation',
        );
      }
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }
  private async importFromAconex() {
    const params = new AconexImportDialogParams({
      documentParams: new AddEditDocumentDialogParams({
        addToOrganisation: true,
        targetOrganisationGid: this.orgId,
        context: 'Organisation',
        mode: AddEditDialogMode.Create,
      }),
    });
    const result = await doAconexImport(params);
    if (result.didImport) {
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }

  // * LIFECYCLE
  private created() {
    this.checkRoutePath();

    if (this.orgId) {
      vxm.breadcrumbs.setOrganisationName({ organisation: this.orgName });
    }
  }
}
